import type { Logger } from "@expert/logging";
import type { GaiaPayloadConfigs, QuickAction as AIQuickAction } from "@expert/shared-types";

import { BackIcon, MarkdownViewer } from "@expert/solve-tools";
import { ActionIcon, Box, Flex, Group, Menu, Space, Stack, Text, UnstyledButton } from "@mantine/core";
import { useCallback, useMemo } from "react";

import type { ExpertAssistMessage } from "../../shared-types";
import type { QuickAction } from "../quick-actions";

import { useSendExpertAssistMessage } from "../../hooks";
import { useAutoscroll } from "../../utils";
import { QuickActionGroup } from "../quick-actions";
import { formatRelativeTime, getGuidancePreviewLabel } from "./guidancePreview";
import { proactiveGuidanceMarkdownOptions } from "./markdownOverrides";
import styles from "./styles.module.css";
import { useProactiveGuidanceHistory } from "./useProactiveGuidanceHistory";

interface Props {
    messages: ExpertAssistMessage[];
    logger: Logger;
    gaiaPayloadProps: GaiaPayloadConfigs;
    scrollRef?: React.RefObject<HTMLDivElement | null>;
}

function toTimelineQuickActions(
    aiQuickActions: AIQuickAction[] | undefined,
    sendMessage: ReturnType<typeof useSendExpertAssistMessage>,
    startAutoscroll: () => void,
): QuickAction[] {
    return (aiQuickActions ?? [])
        .filter((action) => action.type === "SendAssistMessage")
        .map((action) => ({
            value: action.label,
            analytics: {
                actionId: "ProactiveGuidanceQuickAction",
                eventData: { prompt: action.prompt },
            },
            isVisibleAfterClick: true,
            onClick: () => {
                void sendMessage({ text: action.prompt, type: "QuickAction", source: "ProactiveGuidance" });
                startAutoscroll();
            },
        }));
}

export function ProactiveGuidanceMessageBubble({ messages, logger, gaiaPayloadProps, scrollRef }: Props) {
    const { guidanceMessages, currentMessage, shouldShowNav, page, total, setPage } =
        useProactiveGuidanceHistory(messages);
    const canGoBack = page > 1;
    const canGoForward = page < total;
    const sendMessage = useSendExpertAssistMessage(logger, gaiaPayloadProps);
    const { start: startAutoscroll } = useAutoscroll(scrollRef);
    const scrollActiveIntoView = useCallback((node: HTMLButtonElement | null) => {
        const container = node?.closest<HTMLElement>("[data-suggestion-scroll]");
        if (!node || !container) return;
        const offset = node.getBoundingClientRect().top - container.getBoundingClientRect().top;
        container.scrollTop += offset - (container.clientHeight - node.clientHeight) / 2;
    }, []);
    const actions = useMemo(
        () => toTimelineQuickActions(currentMessage?.metadata?.aiQuickActions, sendMessage, startAutoscroll),
        [currentMessage, sendMessage, startAutoscroll],
    );
    if (!currentMessage) return null;

    return (
        <Flex className={styles.slot} justify="center">
            <Stack className={styles.card} data-testid="proactive-guidance-message-bubble" gap={0}>
                <Group justify="space-between" align="center" wrap="nowrap">
                    <Text size="sm" c="dark.2">
                        {currentMessage.tag ?? "Suggested guidance"}
                    </Text>
                    {shouldShowNav && (
                        <Group gap="4xs" wrap="nowrap">
                            <ActionIcon
                                aria-label="Previous guidance"
                                className={styles.navButton}
                                disabled={!canGoBack}
                                onClick={() => setPage(page - 1)}
                                size="md"
                                variant="transparent"
                            >
                                <BackIcon height={18} width={18} />
                            </ActionIcon>
                            <Menu position="bottom" width={280} withinPortal>
                                <Menu.Target>
                                    <UnstyledButton aria-label="Jump to a suggestion" className={styles.counterButton}>
                                        <Text c="dark.2" size="xs">
                                            {page} of {total}
                                        </Text>
                                    </UnstyledButton>
                                </Menu.Target>
                                <Menu.Dropdown>
                                    <Menu.Label>Jump to a suggestion</Menu.Label>
                                    <Box className={styles.suggestionScroll} data-suggestion-scroll>
                                        {guidanceMessages.map((message, index) => (
                                            <Menu.Item
                                                className={styles.previewItem}
                                                data-active={index + 1 === page || undefined}
                                                key={message.id}
                                                onClick={() => setPage(index + 1)}
                                                ref={index + 1 === page ? scrollActiveIntoView : undefined}
                                            >
                                                <Stack gap={2}>
                                                    <Text lineClamp={1} size="sm">
                                                        {getGuidancePreviewLabel(message.text)}
                                                    </Text>
                                                    <Text c="dimmed" size="xs">
                                                        {formatRelativeTime(message.timestamp)}
                                                    </Text>
                                                </Stack>
                                            </Menu.Item>
                                        ))}
                                    </Box>
                                </Menu.Dropdown>
                            </Menu>
                            <ActionIcon
                                aria-label="Next guidance"
                                className={styles.navButton}
                                disabled={!canGoForward}
                                onClick={() => setPage(page + 1)}
                                size="md"
                                variant="transparent"
                            >
                                <BackIcon className={styles.forwardIcon} height={18} width={18} />
                            </ActionIcon>
                        </Group>
                    )}
                </Group>
                <MarkdownViewer content={currentMessage.text} options={proactiveGuidanceMarkdownOptions} />
                <Space h="sm" />
                {actions.length > 0 && (
                    <QuickActionGroup
                        actions={actions}
                        gaiaPayloadProps={gaiaPayloadProps}
                        logger={logger}
                        scrollRef={scrollRef}
                        setIsQuickActionsVisible={() => undefined}
                    />
                )}
            </Stack>
        </Flex>
    );
}
